import { useMemo } from 'react';

import { ListItem, ListItemText, Typography } from '@cast/design-system';
import { ScheduledRebalancingTimeZone } from '@cast/types';

import { useTimeZonesQuery } from 'hooks/queries/scheduled-rebalancing/useTimeZonesQuery';

import { RffAutocomplete } from '@cast/shared-rff-ui';

type TimeZone = ScheduledRebalancingTimeZone & { displayName?: string };

const getDisplayName = (name?: string): string | undefined => {
  if (!name) {
    return;
  }

  const [_, ...parts] = name.split('/');

  return (parts.length ? parts.join('/') : _).replace('_', ' ');
};

export const TimeZoneInput = () => {
  const { timeZones: _timeZones } = useTimeZonesQuery();
  const timeZones: TimeZone[] | undefined = useMemo(() => {
    return _timeZones?.map((tz) => ({
      ...tz,
      displayName: getDisplayName(tz.name),
    }));
  }, [_timeZones]);

  return (
    <RffAutocomplete
      name="timeZone"
      size="sm"
      inputProps={{ label: 'Time zone' }}
      options={timeZones || []}
      optionLabel="displayName"
      optionValue="name"
      endAdornment={(tz: TimeZone | null) =>
        tz ? (
          <Typography variant="inherit" noWrap>
            UTC{tz.offset}
          </Typography>
        ) : null
      }
      renderOption={(option: TimeZone, props) => {
        return (
          <ListItem {...props}>
            <ListItemText
              sx={{
                typography: 'P14R',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
              primary={
                <Typography variant="inherit">{option.displayName}</Typography>
              }
              secondary={
                <Typography variant="inherit">UTC{option.offset}</Typography>
              }
            />
          </ListItem>
        );
      }}
      sx={{ mb: 16 }}
      virtualization
      testId="time-zone-input"
    />
  );
};
